import { Clipboard } from "@capacitor/clipboard";
import { IonAlert } from "@ionic/react";
import { ComponentProps, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";

import Article from "@components/v1/Article";
import styles from "@components/v1/ArticleIntroMobile.module.css";
import DateAndTimeStamp from "@components/v1/dates/DateAndTimeStamp";
import PinToggle from "@components/v1/PinToggle";
import AlertPopover from "@components/v1/popovers/AlertPopover";
import Video from "@components/v1/Video";
import WebinarActions from "@components/v1/WebinarActions";
import CircleButton from "@components/v2/buttons/CircleButton";
import Badge from "@components/v2/graphic_elements/Badge";
import Icon from "@components/v2/icons/Icon";
import Text from "@components/v2/typography/Text";
import useMe from "@hooks/useMe";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useOpener from "@hooks/useOpener";
import useRegisterPlayedVideo from "@hooks/useRegisterPlayedVideo";
import {
  ArticleContentTypeEnum,
  ArticleShareCreateDocument,
  ArticleShareCreateMutation,
  ArticleShareCreateMutationVariables,
  PinResourceTypeEnum
} from "@typing/Generated";
import { articleHasVideo } from "@utils/articleUtils";
import { webinarRecurrenceDescription } from "@utils/modelUtils";

type Props = Pick<ComponentProps<typeof Article>, "article" | "journeyId">;

const ArticleIntroMobile = ({ article, journeyId }: Props) => {
  const intl = useIntl();
  const [shareUrl, setShareUrl] = useState<string | undefined>();
  const date = article.webinarStartTime;
  const isWebinar = ArticleContentTypeEnum.WEBINAR === article.contentType;
  const shareAlertOpener = useOpener();
  const history = useHistory();
  const myUser = useMe();
  const onVideoPlay = useRegisterPlayedVideo(article.id, journeyId);

  const [createArticleShareLink] = useMutationWithValidation<
    ArticleShareCreateMutation,
    ArticleShareCreateMutationVariables
  >(ArticleShareCreateDocument, "articleShare");

  const handleCopyShareUrl = useCallback(() => {
    createArticleShareLink({
      articleId: article.id
    }).then(res => {
      const currentShareUrl = res?.articleShareCreate?.articleShare?.link;
      if (currentShareUrl) {
        Clipboard.write({ string: currentShareUrl }).catch(() => {
          // In safari, doing things like accessing the clipboard have to occur synchronously with a user
          // action. Because we had to go make a request to get the URL, it won't work here. In that case,
          // pop up a special alert to let you copy the link.
          setShareUrl(currentShareUrl);
          shareAlertOpener.open();
        });
      }
    });
  }, [createArticleShareLink, article, shareAlertOpener]);

  return (
    <>
      <div className={styles.intro}>
        {myUser && (
          <div className={styles.backButtonMobile}>
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.back"
              color="white"
              icon="chevronLeft"
              onClick={() => {
                history.goBack();
              }}
              size="large"
            />
          </div>
        )}

        <div className={styles.hero}>
          {articleHasVideo(article) && article.videoUrl ? (
            <div className={styles.video}>
              <Video
                aria-label={article.title}
                onPlay={onVideoPlay}
                poster={article.photoUrl ?? undefined}
                videoUrl={article.videoUrl}
              />
            </div>
          ) : article.photoUrl ? (
            <img
              alt={intl.formatMessage({ id: "accessibility.altTags.article" })}
              className={styles.image}
              src={article.photoUrl}
            />
          ) : null}
        </div>

        <div className={styles.introContent}>
          <div className={styles.attributionLockup}>
            {article.hostedByGather && (
              <Badge color="primary-300" label={intl.formatMessage({ id: "pages.explore.events.hostedByGather" })} />
            )}

            {!isWebinar && (
              <>
                <Icon color="primary-300" icon="logoIcon" size="xlarge" />
                <Text bold>
                  <FormattedMessage id="pages.explore.events.byGrayce" />
                </Text>
              </>
            )}
          </div>

          <div className={styles.utilities}>
            <Text variant="2xlSerif">{article.title}</Text>
            {journeyId && (
              <div className={styles.actions}>
                <PinToggle
                  color="gray-150"
                  journeyId={journeyId}
                  resourceId={article.id}
                  resourceType={PinResourceTypeEnum.ARTICLE}
                />
                <AlertPopover textKey="pages.expert.sharableUrls.copied">
                  <CircleButton
                    ariaLabelKey="accessibility.ariaLabels.share"
                    color="gray-150"
                    fill="clear"
                    icon="shareSquare"
                    id="shareCopied"
                    onClick={handleCopyShareUrl}
                    size="medium"
                  />
                </AlertPopover>
              </div>
            )}
          </div>

          {date && (
            <div className={styles.date}>
              <Text bold color="primary-300" size="lg">
                <DateAndTimeStamp showWeekday value={date} />
              </Text>
              <Text color="primary-300" size="lg">
                {webinarRecurrenceDescription(article, intl)}
              </Text>
            </div>
          )}
          <div className={styles.quote}>
            <Text size="lg">{article.quote}</Text>
          </div>

          {isWebinar && (
            <div className={styles.button}>
              <WebinarActions article={article} isWebinar={isWebinar} journeyId={journeyId} />
            </div>
          )}
        </div>
      </div>

      <IonAlert
        buttons={[
          {
            handler: () => {
              Clipboard.write({ string: shareUrl });
            },
            text: intl.formatMessage({ id: "models.articleShare.copyLink" })
          }
        ]}
        isOpen={shareAlertOpener.isOpen}
        message={intl.formatMessage({ id: "models.articleShare.clickToCopyFromAlert" }, { url: shareUrl })}
        onDidDismiss={shareAlertOpener.close}
      />
    </>
  );
};

export default ArticleIntroMobile;
